// Single source of truth for features that are intentionally not yet
// generally available. Anything missing from this map is treated as
// `'available'` — so existing pages keep working unchanged.
//
// Why a static registry: when a PR ships a half-finished module to `dev`, the
// menu link still works — but instead of showing a crash or an empty spinner,
// the page renders the `<FeatureStatus />` placeholder. The status is reviewed
// in code review like any other constant.
//
// Per-company overrides come from `company.feature_flags` (set by the backend)
// and take precedence over this map — see `useFeatureFlag()`.

export type FeatureStatusKind =
  | 'coming_soon'
  | 'maintenance'
  | 'upgrade_required'
  | 'not_configured'
  | 'beta_unavailable'
  | 'partial_data';

export interface FeatureFlag {
  status: FeatureStatusKind | 'available';
  /** ISO date or human label ("2026-Q3"). Rendered via i18n interpolation. */
  eta?: string;
}

// `import.meta.env.DEV` solo es true bajo `vite dev` local: el dev DESPLEGADO
// (app.dev.verix.es) es un build de producción de Amplify, donde DEV === false
// y los draft quedaban ocultos también en dev. `VITE_ENV` lo inyecta Amplify
// por entorno ("development"/"production"), así que NON_PROD es true en local +
// dev-deployed y false en prod. Mantiene el banner "no disponible" en prod.
const NON_PROD = import.meta.env.DEV || import.meta.env.VITE_ENV !== 'production';

export const FEATURE_REGISTRY: Record<string, FeatureFlag> = {
  // CRM — pipeline + activities are live; opportunities/quotes still partial
  'crm.opportunities':           { status: 'coming_soon', eta: '2026-Q3' },
  'crm.quotes':                  { status: 'coming_soon', eta: '2026-Q3' },

  // Inventory — CRUD ready, automatic reorder/alert engine is not
  'inventory.reorder_alerts':    { status: 'coming_soon' },

  // Tax — Modelo 303/347 read-only; auto-submit to AEAT is pending
  'tax.modelo_303_auto':         { status: 'beta_unavailable' },
  'tax.modelo_347_auto':         { status: 'beta_unavailable' },

  // AI tools — reconectadas al backend 2026-06-02, confirmado sano
  // (toolRegistry.ts) y activadas 2026-08-03.
  'ai.consolidation':            { status: 'available' },
  'ai.scenarios':                { status: 'available' },
  'ai.currency':                 { status: 'available' },
  'ai.pricing':                  { status: 'available' },
  'ai.employee_performance':     { status: 'available' },

  // Plan-gated features
  'multi_company':               { status: 'upgrade_required' },

  // ARIA Home — landing chat-first que sustituye el dashboard en `/`.
  // NON_PROD-available (mismo patrón que payroll.siltra_export): en local +
  // app.dev `/` muestra el ARIA Home; en prod queda OFF (dashboard actual).
  // Motivo: el override por-empresa (company.feature_flags) NO está cableado en
  // el backend todavía — no lo expone /api/auth/me, así que el gate efectivo es
  // este flag de compile-time. La ruta `/aria-home` siempre monta para QA.
  // 2026-07-01: activado en prod para todas las empresas (decisión "todo ON").
  // Se quita el gate NON_PROD → `/` muestra el ARIA Home en todos los entornos.
  'aria.home':                   { status: 'available' },

  // ARIA Home v2 — rediseño "data-cinematográfico" de la landing (escena 3D
  // protagonista + KPIs satélite + chat héroe + señales). Sub-toggle aditivo
  // sobre `aria.home`: solo aplica cuando la ARIA Home ya está activa. Mismo
  // 2026-07-01: activado en prod (decisión "todo ON") → escena 3D cinematográfica
  // ("la bola") + KPIs satélite en todos los entornos. Reversible poniendo coming_soon.
  'aria.home_v2':                { status: 'available' },
  // Backend capability handshake remains mandatory; production rollout is opt-in.
  'aria.adaptive_dashboard':     { status: NON_PROD ? 'available' : 'coming_soon' },

  // Sprint 5.bis.1.b — DEV-only.
  //
  // Why dev-only and not 'available' globally:
  //   The BE runs the saga in stub mode (AEAT_BULK_REAL_SUBMIT=false). If a
  //   prod user clicks "Presentar lote", the BE WILL execute the stub flow
  //   and write synthetic STUB-<hex> receipt_id values to the real
  //   bulk_batch_companies table. The BE double-flag guard prevents AEAT
  //   side effects, NOT database writes — so the FE must keep the CTA
  //   hidden in prod to protect real customer data.
  //
  // Gated por `NON_PROD`, no por `import.meta.env.DEV`. Hasta el 2026-09-07
  // esta entrada usaba `DEV` a secas, que solo es `true` bajo `vite dev` en
  // local: el dev DESPLEGADO (app.dev.verix.es) es un build de producción de
  // Amplify, así que ahí valía `false` y el CTA salía deshabilitado. O sea que
  // el único sitio donde se podía presentar un lote era el portátil de quien
  // programaba — nadie más podía probar el flujo. `NON_PROD` cubre local + dev
  // desplegado y deja prod fuera, que es lo que este flag quería decir.
  //
  // Prod sigue protegido por `VITE_ENV = "production"`, que inyecta el Amplify
  // de prod (`environments/prod/main.tf`). Si esa variable desapareciera de ahí,
  // `NON_PROD` pasaría a `true` en prod y este CTA se encendería: el aviso de
  // arriba sobre los acuses `STUB-<hex>` sobre datos reales sigue vigente.
  //
  // Los dos operandos son constantes de compilación que Vite sustituye, así que
  // el bundle de prod sigue viendo `{ status: 'beta_unavailable' }` sin rama en
  // tiempo de ejecución.
  //
  // Sprint 5.bis.1.c will replace this guard with a per-environment
  // dynamic flag emitted from BE `company.feature_flags` once the real
  // submit path passes AEAT-PRE integration.
  //
  // 2026-09-14: se retira la excepción NON_PROD. El BE ya protege el canal
  // TGVI (informativas) con `AEAT_BULK_REAL_SUBMIT` + whitelist por modelo
  // (default STUB en todos los entornos), pero es un default de config, no
  // un bloqueo por código — si alguien activaba esas dos vars en su propio
  // entorno de dev, el botón de aquí (habilitado bajo NON_PROD) dejaba
  // llegar la llamada real a AEAT-PRE. Este flag es ahora el único gate y
  // se mantiene en `beta_unavailable` en TODOS los entornos, dev incluido.
  'accounting_firm.bulk_submit_aeat': {
    status: 'beta_unavailable',
  },

  // payroll.siltra_export — disparadores OUTBOUND SILTRA (tab SILTRA en Nóminas).
  // MULTI-COUNTRY: SLD = ficheros TGSS España; otros países usarán otros endpoints.
  // DEV-only por ahora: el backend gatea con SILTRA_ADAPTER_DRAFT_ENABLED (503 si OFF).
  // El FE no puede hacer un probe GET (rate-limit 1/min quemaría el budget del 1er
  // generate), así que el gate del banner es este flag de compile-time.
  // TODO: cuando SILTRA_ADAPTER_DRAFT_ENABLED flipee a true en prod (post validación
  // Q-CROWE), actualizar este status a 'available' (o a un flag dinámico vía
  // company.feature_flags). Sin esto el backend funciona pero la UI sigue mostrando el
  // banner "no disponible" en prod — paper trail para el futuro Adrián/Code.
  'payroll.siltra_export': {
    status: NON_PROD ? 'available' : 'beta_unavailable',
  },

  // payroll.siltra_inbound — pantallas INBOUND SILTRA (tab "SILTRA · Recepción").
  // Mismo backend flag que OUTBOUND (SILTRA_ADAPTER_DRAFT_ENABLED → 503 si OFF);
  // DEV-only por ahora. Mantenemos el gate compile-time por simetría con OUTBOUND
  // y para no exponer la UI en prod mientras el adapter está en draft.
  // TODO: flip a 'available' (o flag dinámico vía company.feature_flags) cuando
  // SILTRA_ADAPTER_DRAFT_ENABLED pase a prod (post validación Q-CROWE), junto con
  // payroll.siltra_export.
  'payroll.siltra_inbound': {
    status: NON_PROD ? 'available' : 'beta_unavailable',
  },

  // aria.durable_run — ejecución durable de ARIA (E2). En vez de sostener la
  // respuesta dentro de una conexión, la consulta se persiste como `agent_runs`,
  // la ejecuta un worker con 900 s (sin el techo de ~27 s del camino síncrono) y
  // el cliente la observa por cursor reconectable (`useAriaRun`).
  //
  // ON EN DEV desde 2026-07-28, tras pasar en dev DESPLEGADO el E2E de
  // reconexión por cursor (rompe un sondeo a propósito y comprueba que el
  // reintento reanuda en el mismo `after`). Es el rodaje real: ni los unitarios
  // ni un E2E encuentran lo que encuentra un camino en uso.
  //
  // PROD SIGUE APAGADO, Y NO ES CONSERVADURISMO: allí `AI_SYSTEM_VERSION` es
  // 'v1' y `POST /api/aria/runs` responde 503 por diseño (el ejecutor vive en la
  // rama v2 del worker; con v1 el run quedaría encolado para siempre). Y
  // `useAriaRun` NO cae al camino clásico ante un fallo — marca el error como
  // reintentable. Encenderlo en prod hoy dejaría el chat sin responder.
  //
  // Rollback granular por usuario: `?run=0` fuerza el camino clásico aunque el
  // flag esté ON. `?run=1` lo fuerza al revés (mismo patrón que `?v=1` de
  // invoices.form_v2).
  //
  // FLIP a `'available'` global cuando `AI_SYSTEM_VERSION=v2` en prod. La otra
  // precondición que figuraba aquí —retirar el `while` manual de `agentGraph`—
  // ya está hecha (H14, api#1494).
  //
  // ⛔ OTRA PRECONDICIÓN, ABIERTA (15-10-2026): el run NO COBRA CRÉDITOS. El
  // único `chargeCredits` del backend está en `chatHandler` (`cobrarTurno`);
  // `runs/runExecutor.ts` registra el coste (`cost_eur`, `[aria] turn cost`) pero
  // no lo descuenta del saldo ni comprueba que lo haya. Encender esto en prod sin
  // arreglarlo deja TODO ARIA de prod sin cobrar.
  'aria.durable_run': {
    status: NON_PROD ? 'available' : 'beta_unavailable',
  },
  // payroll.siltra_reconciliation — visualización del cuadre SILTRA (tab "SILTRA · Cuadre").
  // Consume verix-api#588 (GET /api/siltra-reconciliation), gated en BE por el mismo
  // SILTRA_ADAPTER_DRAFT_ENABLED (503 si OFF). DEV-only por ahora, simetría con
  // export/inbound. TODO: flip a 'available' cuando SILTRA_ADAPTER_DRAFT_ENABLED pase
  // a prod (post Q-CROWE), junto con payroll.siltra_export / payroll.siltra_inbound.
  'payroll.siltra_reconciliation': {
    status: NON_PROD ? 'available' : 'beta_unavailable',
  },
};
